/**
 * ADR-0018 — A PROPOSED READING OF THE OPEN EXCEPTION, by a model.
 *
 * Written to be read as a PROPOSAL and never as a finding: it says which model
 * proposed it, of which version, that it changed nothing, and — where Leveri's
 * checks refused it — why, without showing the refused text. The treatment
 * conclusion above it is unchanged by anything here; that is the point.
 *
 * Every label, headline and agreement sentence is the server's. The model's
 * own words appear only in the reading, the hypotheses' statements and the
 * evidence purposes, after the gate admitted them.
 */
import { useState } from "react";
import { WorkActionResponse, type WorkFile } from "@leveri/contracts";
import { Citation, Label, Marker, Notice, RuledList, Section, SectionHead } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";

export interface ProposedReadingProps {
  work: WorkFile;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function ProposedReading({ work, onWork, onOutcome }: ProposedReadingProps) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const reading = work.investigation;
  if (!reading && !work.canInvestigate) return null;

  async function ask() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/investigation`, WorkActionResponse, {});
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const action = work.canInvestigate ? (
    <button type="button" disabled={busy} onClick={() => void ask()} className="text-[13px] text-stone transition-colors hover:text-ink disabled:opacity-50">
      {busy ? "Reading…" : reading?.current ? "Ask again" : "Ask for a proposed reading"}
    </button>
  ) : undefined;

  return (
    <Section id="reading">
      <SectionHead aside={action}>Proposed reading</SectionHead>
      {!reading && (
        <p className="text-sm text-stone">
          A model can read the open item and propose what it most probably is, and which evidence would settle it. It is
          shown here as a proposal, attributed to the model; it changes nothing in this work.
        </p>
      )}
      {reading && (
        <>
          <p className="text-[13px] text-stone">
            {reading.readerText} · {reading.askedByName} · <Citation>{reading.createdAtText}</Citation>
            {!reading.current && <> · of workpaper version {reading.versionSeq}, before the current one</>}
          </p>
          <p className="mt-1 text-[13px] text-ink-soft">{reading.headline}</p>
          {/* The model's words are QUOTED, never set as Leveri's own finding
              (ADR-0011 L3-OBS-07): plain text, ruled off, attributed above. */}
          {reading.disposition === "ADMITTED" && reading.readingWithheld && <p className="mt-3 text-sm text-ink-soft">{reading.readingWithheld}</p>}
          {reading.disposition === "ADMITTED" && reading.reading && (
            <blockquote className="mt-3 border-l-2 border-rule pl-4 text-sm text-ink-soft">{reading.reading}</blockquote>
          )}
          {reading.hypotheses.length > 0 && (
            <div className="mt-4">
              {reading.rankingText && <p className="mb-2 text-sm text-ink-soft" data-testid="investigation-unranked">{reading.rankingText}</p>}
              <Label>{reading.explanationsLabel}</Label>
              <RuledList className="mt-1">
                {reading.hypotheses.map((h, i) => (
                  <div key={`${h.label}-${i}`} className="py-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-ink">
                      {h.leading && <Marker tone="progress" />}
                      {h.label}{h.leading && <span className="font-normal text-stone"> — most likely, in the model's reading</span>}
                    </div>
                    <p className="mt-1 text-sm text-ink-soft">{h.statement}</p>
                    {h.wordedByLeveri && <p className="mt-0.5 text-xs text-ink-soft">In Leveri's words, from the reading's structured cause.</p>}
                    <p className="mt-1 text-[13px] text-stone">
                      {h.citations.length > 0 && <>Rests on {h.citations.join("; ")}. </>}Settled by: {h.settledBy.join("; ").toLowerCase()}.
                    </p>
                  </div>
                ))}
              </RuledList>
            </div>
          )}
          {reading.removed.length > 0 && (
            <div className="mt-4" data-testid="investigation-removed">
              <Label>Removed by Leveri</Label>
              <p className="mt-1 text-[13px] text-stone">Part of the model's reading did not stand up to Leveri's checks and is not shown:</p>
              <ul className="mt-1 list-disc pl-5 text-[13px] text-stone">
                {reading.removed.map((r, i) => <li key={`${r.label}-${i}`}>{r.label} — {r.reason}</li>)}
              </ul>
            </div>
          )}
          {reading.needs.length > 0 && (
            <div className="mt-4">
              <Label>Evidence worth asking for</Label>
              <RuledList className="mt-1">
                {reading.needs.map((n, i) => (
                  <div key={`${n.label}-${i}`} className="py-3">
                    <div className="text-sm text-ink">{n.label}</div>
                    <p className="mt-1 text-[13px] text-stone">{n.purpose}{n.citations.length > 0 && <> ({n.citations.join("; ")})</>}</p>
                  </div>
                ))}
              </RuledList>
              {reading.agreementText && <p className="mt-2 text-[13px] text-stone">{reading.agreementText}</p>}
            </div>
          )}
          {reading.disposition === "REFUSED" && reading.refusedBecause.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-[13px] text-stone">
              {reading.refusedBecause.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
          {reading.disposition === "UNAVAILABLE" && reading.unavailableReason && (
            <p className="mt-2 text-[13px] text-stone">{reading.unavailableReason}</p>
          )}
          {reading.usageText && <p className="mt-3 text-[12px] text-stone">{reading.usageText}</p>}
        </>
      )}
      {failure && <div className="mt-3"><Notice>{failure}</Notice></div>}
    </Section>
  );
}
